/**
 * Subscription plans, and what a complex's plan entitles it to.
 *
 * A platform administrator keeps the plans in the admin console and puts each
 * complex on one. Assigning a plan copies what it entitles onto the complex's
 * own document as `subscription`, which every member can read, so the apps and
 * the rules decide from one document they already have instead of each
 * inventing its own idea of the plan. The functions refuse what the plan does
 * not include; the apps leave it off the screen.
 *
 * Only the extras are entitlements. Billing, payments, verification, the books
 * and repairs are the product and come with every plan.
 *
 * A complex with no `subscription` at all predates plans and keeps everything,
 * with no unit limit, until an administrator puts it on one. Turning features
 * off under a complex that never agreed to a plan would be a surprise nobody
 * chose.
 */

import { assertBirr, type Birr } from "./money.js";

export const ENTITLEMENTS = ["campaigns", "advertisements", "reminders", "telebirr"] as const;
export type Entitlement = (typeof ENTITLEMENTS)[number];

export const ENTITLEMENT_LABELS: Record<Entitlement, string> = {
  campaigns: "Campaigns (residents give towards a project)",
  advertisements: "Contractor listings",
  reminders: "Rent reminders",
  telebirr: "Pay with telebirr",
};

export interface Plan {
  id: string;
  name: string;
  /** What the complex pays Liyu a month, in whole birr. */
  monthlyPrice: Birr;
  /** The most units the complex may have. Null is no limit. */
  unitLimit: number | null;
  entitlements: Entitlement[];
}

/** What sits on `organizations/{orgId}.subscription`. */
export interface Subscription {
  planId: string;
  planName: string;
  monthlyPrice: Birr;
  unitLimit: number | null;
  entitlements: Entitlement[];
  /** The last day paid for, `YYYY-MM-DD` in EAT, or null when not recorded. */
  paidUntil: string | null;
}

export class PlanError extends Error {}

function entitlementsFrom(value: unknown): Entitlement[] {
  if (!Array.isArray(value)) return [];
  return ENTITLEMENTS.filter((known) => value.includes(known));
}

function limitFrom(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : null;
}

/** A plan as stored, checked on the way out of Firestore. */
export function toPlan(id: string, data: Record<string, unknown>): Plan {
  const price = data.monthlyPrice;
  return {
    id,
    name: typeof data.name === "string" ? data.name : id,
    monthlyPrice: typeof price === "number" && Number.isInteger(price) && price >= 0 ? price : 0,
    unitLimit: limitFrom(data.unitLimit),
    entitlements: entitlementsFrom(data.entitlements),
  };
}

/** A plan an administrator typed, refused if it could not be kept. */
export function checkPlan(plan: Omit<Plan, "id">): Omit<Plan, "id"> {
  if (plan.name.trim() === "" || plan.name.length > 60) {
    throw new PlanError("A plan needs a name of at most 60 characters.");
  }
  assertBirr(plan.monthlyPrice, "monthlyPrice");
  if (plan.monthlyPrice < 0) {
    throw new PlanError("A plan's price cannot be negative.");
  }
  if (plan.unitLimit !== null && (!Number.isInteger(plan.unitLimit) || plan.unitLimit < 1)) {
    throw new PlanError("A unit limit is a whole number above zero, or none.");
  }
  return {
    name: plan.name.trim(),
    monthlyPrice: plan.monthlyPrice,
    unitLimit: plan.unitLimit,
    entitlements: ENTITLEMENTS.filter((known) => plan.entitlements.includes(known)),
  };
}

/** The copy of a plan a complex carries. */
export function subscriptionFor(plan: Plan, paidUntil: string | null): Subscription {
  return {
    planId: plan.id,
    planName: plan.name,
    monthlyPrice: plan.monthlyPrice,
    unitLimit: plan.unitLimit,
    entitlements: [...plan.entitlements],
    paidUntil,
  };
}

/** The complex's subscription, or null for one that predates plans. */
export function subscriptionOf(
  organization: Record<string, unknown> | undefined,
): Subscription | null {
  const stored = organization?.subscription;
  if (typeof stored !== "object" || stored === null) return null;
  const data = stored as Record<string, unknown>;
  const price = data.monthlyPrice;
  return {
    planId: typeof data.planId === "string" ? data.planId : "",
    planName: typeof data.planName === "string" ? data.planName : "",
    monthlyPrice: typeof price === "number" && Number.isInteger(price) ? price : 0,
    unitLimit: limitFrom(data.unitLimit),
    entitlements: entitlementsFrom(data.entitlements),
    paidUntil: typeof data.paidUntil === "string" ? data.paidUntil : null,
  };
}

/**
 * What a complex may use, as the apps and functions ask it. Everything for a
 * complex that predates plans.
 */
export type Entitlements = ReadonlySet<Entitlement>;

export const EVERYTHING: Entitlements = new Set(ENTITLEMENTS);

export function entitlementsOf(organization: Record<string, unknown> | undefined): Entitlements {
  const subscription = subscriptionOf(organization);
  return subscription === null ? EVERYTHING : new Set(subscription.entitlements);
}

/** Whether a complex may hold this many units. */
export function withinUnitLimit(
  organization: Record<string, unknown> | undefined,
  units: number,
): boolean {
  const limit = subscriptionOf(organization)?.unitLimit ?? null;
  return limit === null || units <= limit;
}

/** A plan's id from its name: lowercase words joined by hyphens. */
export function planIdFrom(name: string): string {
  const id = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
  if (id === "") {
    throw new PlanError("A plan's name needs a letter or a digit in it.");
  }
  return id;
}
